import { type BrandConfig } from '@sso/brand-configs';
import { OriginPageData, UrlParams } from '@sso/common-types';
import {
  getEnabledSocialSites,
  getIsCheckoutLite,
  isIosNativeApp,
  SocialApp,
  TRAFFIC_RECOMMENDED_WAIT_TIME_IN_MS,
} from '@sso/core-utils';
import { useEffect, useRef } from 'react';

type Props = {
  page: string;
  market: string;
  realm: string;
  isChina: boolean;
  brandConfig: BrandConfig;
  urlParams: UrlParams;
  enableApple: boolean;
  originPageData: OriginPageData;
  socialLoginLaunch: (app: string) => void;
  completeAccount?: boolean;
};

export const socialButtonsExist = (buttonsVisibility?: Record<SocialApp, boolean>): boolean => {
  return buttonsVisibility != null && Object.values(buttonsVisibility).some(Boolean);
};

export const getSocialLoginButtonsVisibility = ({
  page,
  market,
  realm,
  isChina,
  brandConfig,
  urlParams,
  enableApple,
  originPageData,
  completeAccount = false,
}: Omit<Props, 'socialLoginLaunch'>) => {
  const isCheckoutLite = getIsCheckoutLite(urlParams);
  const sites = getEnabledSocialSites({
    page,
    market,
    realm,
    isChina,
    urlParams,
    brandConfig,
  });
  const isAppleButtonVisible = () => {
    // show apple login button only when migration token is present OR enableApple state is true
    return ![undefined, null, ''].includes(originPageData?.migration_token) || enableApple;
  };
  const buttonsVisibility: Record<SocialApp, boolean> = {
    facebook: sites.facebook && completeAccount !== true,
    google: sites.google,
    wechat: sites.wechat && isCheckoutLite !== true && completeAccount !== true,
    amazon: sites.amazon && isCheckoutLite !== true,
    apple: sites.apple && isCheckoutLite !== true && isAppleButtonVisible() === true,
  };

  return buttonsVisibility;
};

export const useSocialLoginButtons = (props: Props) => {
  const { socialLoginLaunch, brandConfig } = props;
  const timerId = useRef<ReturnType<typeof setTimeout>>();
  const buttonsVisibility = getSocialLoginButtonsVisibility(props);

  const handleButtonClick = (appName: string) => {
    const loginLaunch = () => {
      socialLoginLaunch(appName);
      clearTimeout(timerId.current);
    };

    timerId.current = setTimeout(
      loginLaunch,
      isIosNativeApp(brandConfig?.native?.id) ? 0 : TRAFFIC_RECOMMENDED_WAIT_TIME_IN_MS,
    );
  };

  useEffect(() => {
    return () => {
      clearTimeout(timerId.current);
    };
  }, []);

  return {
    buttonsVisibility,
    handleButtonClick,
  };
};
